<template>
  <v-dialog :value="value" max-width="560px" scrollable @input="$emit('input', $event)">
    <v-card v-if="company">
      <v-card-title>
        Vincular a Pipedrive
        <v-spacer />
        <v-btn icon aria-label="Cerrar" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="pt-1">{{ company.name }}<span v-if="company.country"> · {{ company.country }}</span></v-card-subtitle>

      <v-card-text>
        <template v-if="duplicateMatches.length">
          <p class="mb-2">Ya hay empresas parecidas en Pipedrive. ¿Es alguna de estas?</p>
          <v-list dense>
            <v-list-item v-for="match in duplicateMatches" :key="match.id">
              <v-list-item-content>
                <v-list-item-title>{{ match.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small color="primary" depressed :disabled="saving" @click="link(match.id)">Vincular</v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>

        <template v-else>
          <v-text-field
            v-model="term"
            label="Buscar en Pipedrive"
            prepend-inner-icon="mdi-magnify"
            :loading="searching"
            :disabled="saving"
            outlined
            dense
            hide-details
            @keydown.enter.prevent="search"
          />
          <div class="text-right mt-1">
            <v-btn small text :disabled="searching || saving || !term" @click="search">Buscar</v-btn>
          </div>

          <v-list v-if="results.length" dense>
            <v-list-item v-for="org in results" :key="org.pipedriveOrgId">
              <v-list-item-content>
                <v-list-item-title>{{ org.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="d-flex flex-row align-center">
                <v-btn
                  v-if="org.pipedriveUrl"
                  icon
                  small
                  :href="org.pipedriveUrl"
                  target="_blank"
                  rel="noopener"
                  aria-label="Ver en Pipedrive"
                >
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
                <v-btn small color="primary" depressed :disabled="saving" class="ml-1" @click="link(org.pipedriveOrgId)">
                  Vincular
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-else-if="searched && !searching" class="org-link__empty mt-3 mb-0">
            No encontramos empresas parecidas en Pipedrive.
          </p>
        </template>

        <v-alert v-if="errorText" type="error" dense text class="mt-3 mb-0">{{ errorText }}</v-alert>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn v-if="duplicateMatches.length" text :disabled="saving" @click="duplicateMatches = []">Volver</v-btn>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn
          text
          :loading="saving"
          class="create-org-btn"
          @click="create(duplicateMatches.length > 0)"
        >
          {{ duplicateMatches.length ? 'Crear de todas formas' : 'Crear en Pipedrive' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import { errorMessage } from '../utils/org-chart.utils';

// Vincula una empresa de la investigación (sin Pipedrive) a una organización de Pipedrive,
// o la crea. Emite 'linked' con { pipedriveOrgId }.
export default {
  name: 'LinkCompanyModal',
  props: {
    value: { type: Boolean, default: false },
    // { id | companyId, name, country }
    company: { type: Object, default: null }
  },
  data() {
    return {
      term: '',
      results: [],
      duplicateMatches: [],
      searching: false,
      searched: false,
      saving: false,
      errorText: ''
    };
  },
  computed: {
    companyId() {
      if (!this.company) return null;
      return this.company.id !== undefined ? this.company.id : this.company.companyId;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.reset();
      }
    }
  },
  methods: {
    reset() {
      this.term = (this.company && this.company.name) || '';
      this.results = [];
      this.duplicateMatches = [];
      this.searched = false;
      this.saving = false;
      this.errorText = '';
      if (this.term) this.search();
    },
    close() {
      this.$emit('input', false);
    },
    async search() {
      if (!this.term) return;
      this.searching = true;
      this.errorText = '';
      const res = await ClientOrgChartsService.searchOrganizations(this.term);
      this.searching = false;
      this.searched = true;
      if (res.status === 'ok') this.results = res.data || [];
      else this.errorText = errorMessage(res, 'No pudimos buscar en Pipedrive. Intenta de nuevo.');
    },
    async link(pipedriveOrgId) {
      await this.submit({ pipedriveOrgId });
    },
    async create(skipDuplicateCheck) {
      const payload = { create: true, name: this.company.name };
      if (skipDuplicateCheck) payload.skipDuplicateCheck = true;
      await this.submit(payload);
    },
    async submit(payload) {
      this.saving = true;
      this.errorText = '';
      const res = await ClientOrgChartsService.linkArtifactCompany(this.companyId, payload);
      this.saving = false;
      if (res.status === 'ok') {
        this.$emit('linked', { pipedriveOrgId: res.data && res.data.pipedriveOrgId });
        this.close();
        return;
      }
      if (res.code === 'POSSIBLE_DUPLICATE_ORGANIZATION' && res.details && res.details.matches && res.details.matches.length) {
        this.duplicateMatches = res.details.matches;
        return;
      }
      this.errorText = errorMessage(res, 'No pudimos vincular la empresa. Intenta de nuevo.');
    }
  }
};
</script>

<style scoped>
.org-link__empty {
  color: var(--pw-text-muted);
}
</style>
